import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { parseTrigger, type PickerFilter } from "@/components/editor/refPicker/refPickerModel";

/**
 * Watches the text before the cursor for the `@` picker's trigger.
 *
 * It only reports; the picker decides what to show. The rules for what counts
 * as a trigger live in `parseTrigger`, where they are tested, not here.
 *
 * This replaces the citation extension's own `@cite{` detector. Two detectors
 * would open two menus on `@cite{`, and Olivia's spec is explicit that
 * `@cite{` is a filter on the one picker, not a second door.
 */

export interface RefPickerState {
  active: boolean;
  query: string;
  filter: PickerFilter;
  /** Document range the trigger occupies — replaced on insert. */
  from: number;
  to: number;
  /**
   * Where a trigger the author dismissed with Esc begins. While they keep
   * typing that same trigger the picker stays shut — otherwise someone writing
   * "@home" on purpose would have it reopen on every letter. Mapped through
   * edits so text typed earlier in the paragraph does not lose track of it.
   */
  dismissedFrom: number | null;
}

export const INACTIVE: RefPickerState = {
  active: false, query: "", filter: "all", from: 0, to: 0, dismissedFrom: null,
};

export const refPickerPluginKey = new PluginKey<RefPickerState>("refPicker");

export const RefPickerTrigger = Extension.create({
  name: "refPickerTrigger",

  addProseMirrorPlugins() {
    return [
      new Plugin<RefPickerState>({
        key: refPickerPluginKey,
        state: {
          init: () => INACTIVE,
          apply(tr, prev) {
            // Esc closes the picker on this trigger without touching the text.
            if (tr.getMeta(refPickerPluginKey) === "dismiss") {
              return { ...INACTIVE, dismissedFrom: prev.from };
            }
            if (!tr.docChanged && !tr.selectionSet) return prev;

            const dismissedFrom =
              prev.dismissedFrom === null ? null : tr.mapping.map(prev.dismissedFrom);

            const { selection } = tr;
            if (!selection.empty) return INACTIVE;

            const { $from } = selection;
            if (!$from.parent.isTextblock) return INACTIVE;

            // Atom nodes (an existing citation, a reference) read as U+FFFC, so a
            // trigger never reaches back across one.
            const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, "\ufffc");
            const trigger = parseTrigger(textBefore);
            if (!trigger) return INACTIVE;

            const from = $from.pos - trigger.length;
            // Still the trigger they dismissed: stay shut, and keep remembering.
            if (from === dismissedFrom) return { ...INACTIVE, dismissedFrom };

            return {
              active: true,
              query: trigger.query,
              filter: trigger.filter,
              from,
              to: $from.pos,
              dismissedFrom: null,
            };
          },
        },
      }),
    ];
  },
});
